/**
 * Alcance de "precisa de ação" além do sino do header (spec 2026-08-26 §8):
 *  - badge "(N)" no título da aba, visível na barra de abas sem foco;
 *  - notificação do sistema operacional quando a contagem AUMENTA e a pessoa
 *    está longe da tela (outra aba ou outro app), se ela ligou o aviso.
 *
 * Devolve a última chegada (a contagem aumentou) para o sino animar: quem está
 * olhando a tela vê o sino, quem está longe recebe o aviso do sistema. A
 * detecção de "aumentou" é uma só (`prev`) para os dois — não podem divergir.
 *
 * A PERMISSÃO do aviso não é pedida aqui: o header é global e o prompt nativo é
 * tiro único. O convite vive no painel de Tickets (NotificationOptIn) e nas
 * configurações da pessoa.
 */
import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {isAway, notifyPending} from '../ticketsNotify';
import {NEEDS_ACTION_URL} from '../ticketsLinks';
import {useDocumentTitleBadge} from './useDocumentTitleBadge';

export type PendingArrival = {
  /** Cresce a cada chegada: o sino anima de novo mesmo com o mesmo delta. */
  seq: number;
  /** Quantas chegaram de uma vez. */
  delta: number;
};

export const useNeedsActionReach = (
  count: number | undefined
): PendingArrival | null => {
  const navigate = useNavigate();
  // Baseline undefined: o 1º valor (load) só firma a referência; a chegada só
  // conta quando a contagem AUMENTA depois (novo pendente). Queda não conta.
  const prev = useRef<number | undefined>(undefined);
  const [arrival, setArrival] = useState<PendingArrival | null>(null);

  useEffect(() => {
    if (count === undefined) return;
    if (prev.current !== undefined && count > prev.current) {
      const delta = count - prev.current;
      setArrival((last) => ({seq: (last?.seq ?? 0) + 1, delta}));
      // Aviso do SO só para quem NÃO está olhando a tela: quem está vê o sino.
      if (isAway(document.visibilityState, document.hasFocus())) {
        notifyPending(delta, count, () => navigate(NEEDS_ACTION_URL));
      }
    }
    prev.current = count;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  useDocumentTitleBadge(count ?? 0);

  return arrival;
};
